export type CampaignsListUrlState = {
  retailerId: number | null;
  cycleId: number | null;
  year: number | null;
  page: number;
  search: string;
  includeArchived: boolean;
};

const CAMPAIGNS_LIST_RETURN_KEY = 'campaigns:listReturn';

const LIST_PARAM_KEYS = {
  retailerId: 'listRetailerId',
  cycleId: 'listCycleId',
  year: 'listYear',
  page: 'listPage',
  search: 'listSearch',
  includeArchived: 'listIncludeArchived',
} as const;

function parseOptionalPositiveInt(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function parsePage(value: string | null): number {
  return parseOptionalPositiveInt(value) ?? 1;
}

function parseOptionalYear(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 2000 && parsed <= 2100 ? parsed : null;
}

export function parseCampaignsListUrl(search: string): CampaignsListUrlState {
  const params = new URLSearchParams(search);
  return {
    retailerId: parseOptionalPositiveInt(params.get('retailerId')),
    cycleId: parseOptionalPositiveInt(params.get('cycleId')),
    year: parseOptionalYear(params.get('year')),
    page: parsePage(params.get('page')),
    search: params.get('search') ?? '',
    includeArchived: params.get('includeArchived') === 'true',
  };
}

export function buildCampaignsListUrl(state: CampaignsListUrlState): string {
  const params = new URLSearchParams();
  if (state.retailerId) params.set('retailerId', String(state.retailerId));
  if (state.cycleId) params.set('cycleId', String(state.cycleId));
  if (state.year) params.set('year', String(state.year));
  if (state.page > 1) params.set('page', String(state.page));
  if (state.search.trim()) params.set('search', state.search.trim());
  if (state.includeArchived) params.set('includeArchived', 'true');
  const qs = params.toString();
  return qs ? `/campaigns?${qs}` : '/campaigns';
}

export function buildCampaignDetailUrl(campaignId: number, listState: CampaignsListUrlState): string {
  const params = new URLSearchParams();
  if (listState.retailerId) params.set(LIST_PARAM_KEYS.retailerId, String(listState.retailerId));
  if (listState.cycleId) params.set(LIST_PARAM_KEYS.cycleId, String(listState.cycleId));
  if (listState.year) params.set(LIST_PARAM_KEYS.year, String(listState.year));
  if (listState.page > 1) params.set(LIST_PARAM_KEYS.page, String(listState.page));
  if (listState.search.trim()) params.set(LIST_PARAM_KEYS.search, listState.search.trim());
  if (listState.includeArchived) params.set(LIST_PARAM_KEYS.includeArchived, 'true');
  const qs = params.toString();
  return qs ? `/campaigns/${campaignId}?${qs}` : `/campaigns/${campaignId}`;
}

function parseCampaignsListReturnState(search: string): CampaignsListUrlState | null {
  const params = new URLSearchParams(search);
  const hasReturnParams = Object.values(LIST_PARAM_KEYS).some((key) => params.has(key));
  if (!hasReturnParams) return null;

  return {
    retailerId: parseOptionalPositiveInt(params.get(LIST_PARAM_KEYS.retailerId)),
    cycleId: parseOptionalPositiveInt(params.get(LIST_PARAM_KEYS.cycleId)),
    year: parseOptionalYear(params.get(LIST_PARAM_KEYS.year)),
    page: parsePage(params.get(LIST_PARAM_KEYS.page)),
    search: params.get(LIST_PARAM_KEYS.search) ?? '',
    includeArchived: params.get(LIST_PARAM_KEYS.includeArchived) === 'true',
  };
}

export function saveCampaignsListReturnState(state: CampaignsListUrlState): void {
  try {
    sessionStorage.setItem(CAMPAIGNS_LIST_RETURN_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage may be unavailable in private browsing.
  }
}

export function loadCampaignsListReturnState(): CampaignsListUrlState | null {
  try {
    const raw = sessionStorage.getItem(CAMPAIGNS_LIST_RETURN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CampaignsListUrlState>;
    return {
      retailerId: parseOptionalPositiveInt(
        parsed.retailerId === null || parsed.retailerId === undefined
          ? null
          : String(parsed.retailerId),
      ),
      cycleId: parseOptionalPositiveInt(
        parsed.cycleId === null || parsed.cycleId === undefined
          ? null
          : String(parsed.cycleId),
      ),
      year: parseOptionalYear(
        parsed.year === null || parsed.year === undefined ? null : String(parsed.year),
      ),
      page: parsePage(
        parsed.page === null || parsed.page === undefined ? null : String(parsed.page),
      ),
      search: typeof parsed.search === 'string' ? parsed.search : '',
      includeArchived: parsed.includeArchived === true,
    };
  } catch {
    return null;
  }
}

export function buildCampaignsListUrlFromReturnParams(search: string): string {
  const fromParams = parseCampaignsListReturnState(search);
  if (fromParams) return buildCampaignsListUrl(fromParams);

  const fromSession = loadCampaignsListReturnState();
  if (fromSession) return buildCampaignsListUrl(fromSession);

  return '/campaigns';
}

export function appendListReturnParams(path: string, search: string): string {
  const source = new URLSearchParams(search);
  const returnParams = new URLSearchParams();
  for (const key of Object.values(LIST_PARAM_KEYS)) {
    const value = source.get(key);
    if (value !== null) returnParams.set(key, value);
  }
  if (!returnParams.toString()) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${returnParams.toString()}`;
}

export function syncCampaignsListUrlToBrowser(state: CampaignsListUrlState): void {
  try {
    const url = new URL(window.location.href);
    if (state.retailerId) url.searchParams.set('retailerId', String(state.retailerId));
    else url.searchParams.delete('retailerId');
    if (state.cycleId) url.searchParams.set('cycleId', String(state.cycleId));
    else url.searchParams.delete('cycleId');
    if (state.page > 1) url.searchParams.set('page', String(state.page));
    else url.searchParams.delete('page');
    if (state.search.trim()) url.searchParams.set('search', state.search.trim());
    else url.searchParams.delete('search');
    if (state.includeArchived) url.searchParams.set('includeArchived', 'true');
    else url.searchParams.delete('includeArchived');
    window.history.replaceState({}, '', url.toString());
  } catch {
    // URL may be unavailable during SSR or restricted environments.
  }
}
